'use client';

/**
 * VendorBriefCard — schema-driven entity brief (rail).
 *
 * Reads `entity_schema` from total-matrix-store (zustand-cached layout
 * config) and dispatches each declared section to a mode4-native
 * primitive via `<SectionRouter />`. The schema is layout — strategy
 * selection + record extraction is what drives state.
 *
 * Strategy routing per type (richer-than-entity_details where it
 * exists; fallback to entity_details otherwise):
 *
 *   vendor      → graph_vendor_explore  (vendor + collections + uncategorized_products)
 *   product     → graph_product_explore (FullProductDetail with images.gallery_urls)
 *   collection  → entity_details        (raw node — no richer strategy today)
 *   *           → entity_details
 *
 * Record normalization — each strategy returns a different envelope;
 * we extract the entity-shaped record and pass it to the schema
 * dispatcher with the right product/collection arrays inlined where
 * the schema's `field=` references expect them.
 *
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="vendor_brief"
 * @see klyde_edge/ui_config/entity_schema.py — ENTITY_SCHEMA per type
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useMemo } from 'react';
import { Loader2, Maximize2 } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCardExport } from '@/lib/design/card-export';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useEntitySchema, useTotalMatrixStore } from '@/lib/stores/total-matrix-store';
import { useAgentCanvasStore } from '@/lib/stores/agent-canvas-store';
import { usePricingBenchmarkSelectionStore } from '@/lib/stores/pricing-benchmark-store';
import type { AskRailTurn } from '@/lib/stores/ask-rail-store';
import type { ProductExploreResponse, VendorExploreResponse } from '@/lib/api/types';
import { RailCard } from '../rail-card';
import { SectionRouter } from '../sections/section-router';

/**
 * Build a synthetic AskRailTurn for opening the concertina overlay
 * with a specific renderer card. The Ask Klyde response pipeline
 * normally populates these fields from an LLM round-trip; for a
 * "rail expand" turn the fields are placeholders — the overlay only
 * reads `renderer_card`, and the mounted card sources its data via
 * its own hooks (viewport selection + strategy fetch).
 */
function buildRenderTurn(rendererCard: string, query: string): AskRailTurn {
  return {
    query,
    response: {
      conversation_id: `rail-expand-${Date.now()}`,
      turn: 0,
      short_answer: query,
      long_answer: '',
      response_type: 'answer',
      confidence: 'none',
      sources: [],
      followup_suggestions: [],
      total_ms: 0,
      usage: { input_tokens: 0, output_tokens: 0, total_tokens: 0 },
      renderer_card: rendererCard,
    },
    timestamp: Date.now(),
  };
}

/** Map (entityType) → canvas card key that the overlay should mount.
 *  Returns null when there's no expanded paint for this entity yet —
 *  the affordance hides for those. Adding a new entity = one row. */
const EXPAND_CARD_FOR_ENTITY: Record<string, string> = {
  product: 'product_detail',
};

const ENTITY_DETAILS_STRATEGY = 'entity_details';
const PRODUCT_EXPLORE_STRATEGY = 'graph_product_explore';
const VENDOR_EXPLORE_STRATEGY = 'graph_vendor_explore';

interface EntityDetailsResponse {
  entity?: Record<string, unknown>;
  labels?: string[];
  found?: boolean;
}

export function VendorBriefCard() {
  const { entityType, entityId, mode, contentSelection } = useViewportSelection();
  const schema = useEntitySchema(entityType ?? '');
  const benchmarkContext = usePricingBenchmarkSelectionStore((s) => s.benchmarkContext);
  const enabled = !!entityType && !!entityId && !!schema;

  // Expand-into-overlay affordance — opens the concertina with the
  // matching paint. Two paths:
  //
  //   1. Matrix-driven (preferred) — the current (mode, selection)'s
  //      preset ships `overlay_header_card` / `overlay_body_cards`.
  //      Call openMatrix() with no turn; the overlay reads the
  //      composition from the matrix and paints via LayoutEngine.
  //      This is the cards-everywhere path (mode4_product today).
  //
  //   2. Legacy (Ask Klyde / single-card) — the preset has no overlay
  //      composition. Push a synthetic turn with renderer_card =
  //      EXPAND_CARD_FOR_ENTITY[entityType] (currently `product_detail`
  //      monolith), opening the legacy DocsAnswerLayout-flavoured path.
  //      Retires once every entity gets a preset-side overlay.
  const openOverlayWithTurn = useAgentCanvasStore((s) => s.openWith);
  const openOverlayMatrix = useAgentCanvasStore((s) => s.openMatrix);
  const getViewportConfig = useTotalMatrixStore((s) => s.getViewportConfig);
  // Selection chain MUST match the rest of the card — `useViewportSelection`
  // derives via explorerSelection > selectedNode.type > tab.type, which is
  // the same chain the matrix's `(mode, selection)` lookup uses elsewhere.
  // Reaching for raw `activeTab.type` instead would misroute when a product
  // node is clicked inside a vendor tab (the tab stays 'vendor', the
  // content_selection becomes 'product') — landing on mode4_catalog (no
  // overlay) instead of mode4_product (has overlay), silently dropping
  // back to the legacy turn-driven path.
  const overlayViewport = getViewportConfig(mode, contentSelection);
  const hasMatrixOverlay =
    !!overlayViewport?.overlay_header_card ||
    (overlayViewport?.overlay_body_cards?.length ?? 0) > 0;

  const expandCard = entityType ? EXPAND_CARD_FOR_ENTITY[entityType] : undefined;
  const expandActions =
    enabled && (hasMatrixOverlay || expandCard) ? (
      <button
        onClick={() => {
          if (hasMatrixOverlay) {
            openOverlayMatrix();
          } else if (expandCard) {
            openOverlayWithTurn(
              buildRenderTurn(expandCard, `Expand ${entityType} brief`),
            );
          }
        }}
        className="rounded p-1 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        title="Expand into canvas"
        aria-label="Expand into canvas"
      >
        <Maximize2 className="h-3.5 w-3.5" />
      </button>
    ) : undefined;

  const isVendor = enabled && entityType === 'vendor';
  const isProduct = enabled && entityType === 'product';
  const useEntityDetails = enabled && !isVendor && !isProduct;

  const vendorQuery = useStrategyData<VendorExploreResponse>(
    VENDOR_EXPLORE_STRATEGY,
    isVendor ? { semantic_id: entityId ?? '' } : {},
    isVendor,
  );
  const productQuery = useStrategyData<ProductExploreResponse>(
    PRODUCT_EXPLORE_STRATEGY,
    isProduct ? { semantic_id: entityId ?? '' } : {},
    isProduct,
  );
  const entityQuery = useStrategyData<EntityDetailsResponse>(
    ENTITY_DETAILS_STRATEGY,
    useEntityDetails ? { entity_id: entityId ?? '', entity_type: entityType ?? '' } : {},
    useEntityDetails,
  );

  const active = isVendor ? vendorQuery : isProduct ? productQuery : entityQuery;
  const { isLoading, error } = active;

  // Scene export — query-backed; the active strategy varies by entity
  // kind (vendor / product / other). Shared card: serves mode4's
  // vendor_brief AND mode3's intent_product_brief. Summary scene
  // (identity + name); the full SectionRouter paint stays on screen.
  useCardExport(useMemo(() => {
    const d = active.data?.data as Record<string, unknown> | undefined;
    const v = (d?.vendor ?? d?.product ?? d) as Record<string, unknown> | undefined;
    const name = (v?.name as string) ?? null;
    const status: 'loading' | 'loaded' | 'error' | 'idle' = !enabled
      ? 'idle' : isLoading ? 'loading' : error ? 'error' : v ? 'loaded' : 'idle';
    const query_key = !enabled
      ? null
      : isVendor
        ? ['strategy-data', VENDOR_EXPLORE_STRATEGY, { semantic_id: entityId ?? '' }]
        : isProduct
          ? ['strategy-data', PRODUCT_EXPLORE_STRATEGY, { semantic_id: entityId ?? '' }]
          : ['strategy-data', ENTITY_DETAILS_STRATEGY, { entity_id: entityId ?? '', entity_type: entityType ?? '' }];
    return {
      label: 'Brief',
      content_key: 'vendor_brief',
      surface: 'rail' as const,
      status,
      query_key,
      text: name ? `${entityType}: ${name}` : 'Brief',
      json: v ? { entity_type: entityType ?? null, entity_id: entityId ?? null, name } : null,
    };
  }, [active.data, enabled, isLoading, error, isVendor, isProduct, entityType, entityId]));

  if (!enabled) {
    return (
      <RailCard title="" actions={expandActions}>
        <div className={text.muted}>
          {!entityType || !entityId
            ? 'Select an entity to see details'
            : `No schema registered for "${entityType}"`}
        </div>
      </RailCard>
    );
  }

  if (isLoading) {
    return (
      <RailCard title="" actions={expandActions}>
        <div className={cn(text.muted, 'flex items-center gap-2')}>
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading…
        </div>
      </RailCard>
    );
  }

  // Normalize per-strategy envelope → single record shape the schema
  // sections can read. Keeps top-level keys aligned with what the
  // entity_schema's `field=` references expect.
  let record: Record<string, unknown> | null = null;

  if (isVendor) {
    const r = vendorQuery.data?.data;
    const v = r?.vendor;
    if (v) {
      record = {
        ...v,
        // metadata carries the rolled-up counts; flatten them so the
        // STATS section's `fields=["product_count","collection_count"]`
        // resolves cleanly off the record.
        product_count: r?.metadata?.product_count ?? 0,
        collection_count:
          r?.metadata?.collection_count ?? (v.collections?.length ?? 0),
        // collections passed through verbatim — COLLECTIONS section
        // reads it directly via `field="collections"`.
        collections: v.collections ?? [],
        // Flatten uncategorized_products as a `products` array — the
        // PRODUCTS section's `field="products"` expects a flat list
        // of top products. Schema `max_items=4` truncates display.
        products: v.uncategorized_products ?? [],
      };
    }
  } else if (isProduct) {
    const p = productQuery.data?.data?.product;
    if (p) record = p as unknown as Record<string, unknown>;
    // Benchmark-ladder drill-down: the click-a-track handler stashes a
    // sentence keyed by the product it resolved as nearest. Merge it
    // onto the record ONLY when it matches the selected product, so
    // selecting an unrelated product elsewhere never shows stale
    // benchmark text. See ../../canvas/cards/products/
    // pricing-benchmark-ladder-card.tsx and entity_schema.py's
    // "product" schema (benchmark_context section).
    if (record && benchmarkContext?.productSid === (record as { semantic_id?: string }).semantic_id) {
      record = { ...record, benchmark_context: benchmarkContext.sentence };
    }
  } else {
    const e = entityQuery.data?.data?.entity;
    if (e) record = e;
  }

  if (error || !record) {
    return (
      <RailCard title="" actions={expandActions}>
        <div className={text.muted}>
          {error ? `Failed: ${(error as Error).message}` : 'No data for this entity'}
        </div>
      </RailCard>
    );
  }

  return (
    <RailCard title="" actions={expandActions}>
      <div className="space-y-3">
        {schema!.sections.map((cfg, i) => (
          <SectionRouter
            key={`${cfg.type}-${i}`}
            config={cfg}
            data={record!}
            entityType={entityType!}
            entityId={entityId!}
          />
        ))}
      </div>
    </RailCard>
  );
}
